import { Skeleton, Tooltip } from 'antd';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../../assets/theme';
import { ActivityResults, Participant } from '../../../services/backendService/types';
import { notUndefined } from '../../../utils/general';
import { toIcon } from '../../../utils/roundUtils';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { Slide } from '../../SlideComponents/slide.types';

const BASE_COLOR = '#56bcf7';
const ReportParticipantMatrix = ({
  activityResults,
  participantsList,
  setSelectedTab,
  setSelectedParticipant,
  setSelectedSlide,
  reportTab,
  showTotalParticipationPercentage = true,
  onScrollIntoView,
}: {
  activityResults?: ActivityResults;
  participantsList: Participant[];
  setSelectedTab: (tab: string) => void;
  setSelectedSlide: Dispatch<SetStateAction<Slide | undefined>>;
  setSelectedParticipant?: Dispatch<Participant | undefined>;
  showTotalParticipationPercentage?: boolean;
  reportTab: 'students' | 'summary';
  onScrollIntoView?: (slide: Slide) => void;
}) => {
  const acivitiesWithResults = useMemo(() => {
    if (!activityResults) return [];
    return activityResults.slidesResults.map(r => {
      if (!r.wasPlayed) return undefined;
      return r;
    });
  }, [activityResults]);

  const numberOfPlayedActivities = useMemo(() => {
    if (!acivitiesWithResults) return 0;
    return acivitiesWithResults.filter(r => r).length;
  }, [acivitiesWithResults]);

  const handleActivityClick = useCallback(
    (slide: Slide) => {
      if (!setSelectedTab) return;
      if (reportTab === 'summary') {
        setSelectedTab('activity');
        setSelectedSlide(slide);
      }
      if (reportTab === 'students' && onScrollIntoView) {
        onScrollIntoView(slide);
      }
    },
    [onScrollIntoView, reportTab, setSelectedSlide, setSelectedTab],
  );
  const participationData: number[] = useMemo(() => {
    if (!acivitiesWithResults) return [];
    return participantsList.map(p => {
      // checks if the participant has played in all activities
      const participatedSlides = acivitiesWithResults
        .map(slide => {
          if (!slide) return undefined;
          const score = slide.scores.find(s => s.response.uid === p.id);
          if (!score) return undefined;
          return slide.slides[0].id;
        })
        .filter(notUndefined);
      if (!numberOfPlayedActivities) return 0;
      const participation = participatedSlides.length;
      const participationPercentage = Math.ceil(
        100 * (participation / numberOfPlayedActivities),
      );

      return participationPercentage;
    });
  }, [acivitiesWithResults, numberOfPlayedActivities, participantsList]);
  const handleParticipantClick = useCallback(
    (participant: Participant) => {
      if (reportTab === 'summary') {
        if (setSelectedParticipant) {
          setSelectedParticipant(participant);
          setSelectedTab('students');
        }
      }
    },
    [reportTab, setSelectedParticipant, setSelectedTab],
  );
  const handleParticipantAndActivityClick = useCallback(
    (participant: Participant, slide: Slide) => {
      if (reportTab === 'summary') {
        if (setSelectedParticipant) {
          setSelectedParticipant(participant);
          setSelectedSlide(slide);
          setSelectedTab('students');
        }
      }
      if (reportTab === 'students' && onScrollIntoView) {
        onScrollIntoView(slide);
      }
    },
    [
      onScrollIntoView,
      reportTab,
      setSelectedParticipant,
      setSelectedSlide,
      setSelectedTab,
    ],
  );
  const totalParticipationPercentage = useMemo(() => {
    if (!showTotalParticipationPercentage) return;
    if (!participationData) return;
    if (participationData.length === 0) return;
    const total = participationData.reduce((a, b) => a + b, 0);
    const percentage = Math.ceil(total / participationData.length);
    const color = getParticipantPercentageColor(percentage / 100);

    return { color, percentage };
  }, [participationData, showTotalParticipationPercentage]);
  const slideColumns = useMemo(() => {
    if (!acivitiesWithResults) return;
    return acivitiesWithResults
      .map(slideResults => {
        if (!slideResults) return undefined;
        const slide = slideResults.slides[0];
        const { icon } = toIcon(slide.type);
        const headerCell = (
          <ActivityCell
            key={slide.id}
            onClick={() => handleActivityClick(slide)}
            $header={true}
          >
            <IconWrapper>{icon}</IconWrapper>
          </ActivityCell>
        );
        const participantData = participantsList.map(p => {
          const didParticipate = !!slideResults.scores.find(s => s.response.uid === p.id);

          return (
            <Tooltip
              mouseEnterDelay={1}
              key={p.id}
              title={
                didParticipate
                  ? 'Click to see students response'
                  : 'Student did not participate'
              }
            >
              <ActivityCell
                style={{
                  backgroundColor: didParticipate ? BASE_COLOR : theme.colors.grey,
                }}
                onClick={() => handleParticipantAndActivityClick(p, slide)}
              ></ActivityCell>
            </Tooltip>
          );
        });

        return [headerCell, ...participantData];
      })
      .filter(notUndefined);
  }, [
    acivitiesWithResults,
    handleActivityClick,
    handleParticipantAndActivityClick,
    participantsList,
  ]);
  if (!slideColumns || !activityResults || !participantsList)
    return (
      <MaxWidthContainer>
        <MatrixContainer>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10,1fr)' }}>
            {Array.from({ length: 30 }).map((_, i) => (
              <SkeletonCell key={i} active={true} />
            ))}
          </div>
        </MatrixContainer>
      </MaxWidthContainer>
    );

  return (
    <MaxWidthContainer>
      <TooltipContainer>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TooltipContainerSquare style={{ backgroundColor: BASE_COLOR }} />
          <p style={{ margin: '0px 5px' }}>Participated</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TooltipContainerSquare style={{ backgroundColor: '#d9d9d9' }} />
          <p style={{ margin: '0px 5px' }}>Did not participate</p>
        </div>
      </TooltipContainer>
      <MatrixContainer>
        <NameColumn>
          <StudentNameCell $header={true}>Name</StudentNameCell>
          {participantsList.map(p => (
            <StudentNameCell
              key={p.id}
              onClick={() => handleParticipantClick(p)}
              $isClickable={true}
            >
              {p.name || p.nickName}
            </StudentNameCell>
          ))}
        </NameColumn>
        <ParticipationColumn>
          <ParticipationCell
            $header={true}
            style={
              totalParticipationPercentage
                ? { backgroundColor: totalParticipationPercentage.color }
                : {}
            }
          >
            {totalParticipationPercentage
              ? `${totalParticipationPercentage.percentage} %`
              : ``}
          </ParticipationCell>
          {participationData.map((p, index) => {
            const color = getParticipantPercentageColor(p / 100);

            return (
              <ParticipationCell
                key={`participation-${index}`}
                style={{ backgroundColor: color, justifyContent: 'center' }}
              >
                {p} %
              </ParticipationCell>
            );
          })}
        </ParticipationColumn>
        {slideColumns.map((column, i) => {
          return (
            <SlideColumn key={i}>
              {column.map((cell, j) => {
                return <SlideColumnCell key={`${i}${j}`}>{cell}</SlideColumnCell>;
              })}
            </SlideColumn>
          );
        })}
      </MatrixContainer>
    </MaxWidthContainer>
  );
};

function interpolateColor(color1: string, color2: string, factor: number) {
  const r1 = parseInt(color1.substring(1, 3), 16);
  const g1 = parseInt(color1.substring(3, 5), 16);
  const b1 = parseInt(color1.substring(5, 7), 16);

  const r2 = parseInt(color2.substring(1, 3), 16);
  const g2 = parseInt(color2.substring(3, 5), 16);
  const b2 = parseInt(color2.substring(5, 7), 16);

  const r = Math.round(r1 + (1 - factor) * (r2 - r1));
  const g = Math.round(g1 + (1 - factor) * (g2 - g1));
  const b = Math.round(b1 + (1 - factor) * (b2 - b1));

  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}
export const getParticipantPercentageColor = (percentage: number) => {
  const color1 = BASE_COLOR;
  const color2 = '#d9d9d9';

  return interpolateColor(color1, color2, percentage);
};

const MaxWidthContainer = styled.div`
  width: 100%;
  position: 'relative';
`;
const NameColumn = styled.div``;
const SkeletonCell = styled(Skeleton.Input)`
  min-width: 30px !important;
  border-radius: 5px;
  width: 30px !important;
  margin: 2px;
`;
const SlideColumn = styled.div``;
const ParticipationColumn = styled.div``;
const SlideColumnCell = styled.div``;
const DefaultCell = styled.div<{ $header?: boolean }>`
  cursor: pointer;
  height: 30px;
  width: 50px;
  margin: 2px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow-x: hidden;
  padding: 2px;
  border-radius: 5px;
  ${props =>
    props.$header &&
    css`
      border-bottom: 1px solid ${theme.colors.grey};
      border-radius: 5px 5px 0px 0px;
      margin-bottom: 5px;
    `}
`;
const StudentNameCell = styled(DefaultCell)<{ $isClickable?: boolean }>`
  width: 80px;
  line-height: 1;
  font-size: 12px;
  ${props =>
    props.$isClickable &&
    css`
      &:hover {
        cursor: pointer;
        text-decoration: underline;
        background-color: ${theme.colors.greyLight};
      }
    `}
`;

const ParticipationCell = styled(DefaultCell)`
  justify-content: center;
`;

const TooltipContainer = styled.div`
  position: absolute;
  right: 10px;
  top: 10px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  p {
    margin: 0;
    font-size: 10px;
  }
`;
const TooltipContainerSquare = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 2px;
`;
const MatrixContainer = styled.div`
  padding-right: 10px;
  position: relative;
  width: 100%;
  max-height: 500px;
  overflow: auto;
  display: grid;
  grid-auto-flow: column;
  @media (max-width: 555px) {
    max-width: 80vw;
  }
  padding-bottom: 20px;
  justify-content: start;

  /* flex-direction: row; */
`;
const ActivityCell = styled(DefaultCell)`
  width: 30px;
  justify-content: center;
  &:hover {
    cursor: pointer;
    background-color: ${theme.colors.greyLight};
  }
`;

export default ReportParticipantMatrix;
